import CollapsableCard from './CollapsableCard';
import { ClipboardDocumentListIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import CardListItem from '@/components/ui/custom/dataDisplay/CardListItem';
import PendingHomeworkItem from './PendingHomeworkItem';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { useQueryClient } from '@tanstack/react-query';
import useAuth from '@/data/hook/useAuth';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { pendingHomeworksQueryKeys } from '@/data/services/querykeys';
import useStatisticService from '@/data/hook/useStatistics';
import { ClassNameComponent } from '@/utils/ClassNameComponent';

type PendingHomeworksProps = {
  className?: ClassNameComponent;
};

export default function PendingHomeworks({ className }: PendingHomeworksProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'teacherDashboard.pendingHomeworks',
  });
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const filters = {
    teacherId: user?.id,
  };

  const { queryKey: pendingHomeworksQuerykey, queryFn } =
    pendingHomeworksQueryKeys.list(filters)._ctx.infinity;

  const {
    results: pendingHomeworks,
    isFetching: isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteService({
    queryKey: pendingHomeworksQuerykey,
    queryFn,
  });

  const statisticsOptions =
    pendingHomeworksQueryKeys.list(filters)._ctx.totalSum;
  const { statistic } = useStatisticService(statisticsOptions);

  const pendingCountQuerykey = statisticsOptions.queryKey;

  const onClickRefresh = () => {
    queryClient.invalidateQueries(pendingHomeworksQuerykey);
    queryClient.invalidateQueries(pendingCountQuerykey);
  };
  const totalPending = statistic?.totalPendingHomeworksCount;

  const shouldRenderList = isLoading || pendingHomeworks?.length;
  return (
    <CollapsableCard
      className={className}
      headerIcon={<ClipboardDocumentListIcon className="w-5 stroke-[2]" />}
      title={t('homeworkForCorrection').toUpperCase()}
      isCollapsable={!!pendingHomeworks.length}
      color="orange"
      isLoading={isLoading}
      badgeCount={totalPending}
      header={
        <IconButton
          onClick={onClickRefresh}
          icon={<ArrowPathIcon className="w-6" />}
        />
      }
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
    >
      <ConditionalRenderer
        fallback={
          <div className="flex justify-center text-center items-center h-full flex-col gap-y-2 text-warning font-500">
            <Text text={t('noHomeworks')} />
            <Text text="\ (•◡•) /" />
          </div>
        }
        condition={shouldRenderList}
      >
        {pendingHomeworks.map((pendingHomework, i) => (
          <CardListItem
            color={i % 2 === 0 ? 'orange' : 'blue'}
            key={pendingHomework.scheduledLesson.id}
          >
            <PendingHomeworkItem pendingHomework={pendingHomework} />
          </CardListItem>
        ))}
      </ConditionalRenderer>
    </CollapsableCard>
  );
}
